import { cachedFnCall } from './cachedFn';
import { getUrl } from './helpers';
import { getLocale } from './localeExtraction';

const { fetch } = window;

// Pull the top-level VideoObject out of a schema, whether it's a raw VideoObject root or wrapped
// in a schema.org `@graph`. Used both to read the incoming schema's @id and to walk what's
// already on the page.
const findTopLevelVideoObject = (node) => {
  if (!node || typeof node !== 'object') return null;
  if (Array.isArray(node['@graph'])) {
    return node['@graph'].find((n) => n && n['@type'] === 'VideoObject') || null;
  }
  if (node['@type'] === 'VideoObject') return node;
  return null;
};

// True if any `<script type="application/ld+json">` on the page already contains a VideoObject
// node whose @id equals `videoObjectId`. Catches both our own previously-injected scripts and
// any server-rendered schema a merchant published themselves.
const videoObjectIdAlreadyOnPage = (videoObjectId) => {
  if (!videoObjectId) return false;
  const scripts = document.querySelectorAll('script[type="application/ld+json"]');
  for (const script of scripts) {
    let parsed;
    try {
      parsed = JSON.parse(script.textContent || '');
    } catch {
      continue;
    }
    const videoObject = findTopLevelVideoObject(parsed);
    if (videoObject && videoObject['@id'] === videoObjectId) return true;
  }
  return false;
};

const urlParams = new URLSearchParams(window.location.search);
const isDebug = urlParams.get('debug-prod') || process.env.NODE_ENV === 'development';

export function injectLdJsonSchemaScriptTag(showId, schema = {}) {
  // Reject non-object inputs (string/number/array/etc) — without this guard a stringified
  // payload would be JSON-stringified into the page as garbage structured data.
  if (!schema || typeof schema !== 'object' || Array.isArray(schema) || Object.keys(schema).length === 0) {
    return;
  }

  // Skip if the incoming VideoObject (matched by @id) is already represented on the page by some
  // other ld+json script tag — either one we injected previously or one the merchant has
  // server-rendered themselves.
  const topVideoObject = findTopLevelVideoObject(schema);
  if (topVideoObject && videoObjectIdAlreadyOnPage(topVideoObject['@id'])) {
    return;
  }

  try {
    // See if we find any BambuserInlinePlayerComponent web-components on the page,
    // and see if that components show-id matches the showId for the SEO schema we're trying to inject,
    // if so we can skip adding the "mainEntityOfPage" property to the schema as video is already presented
    // in the current context without additional nudging in form of the autoplayLiveShopping query param.
    const isSeoForInlinePlayer = Array
      .from(document.querySelectorAll('bam-inline-player'))
      .some((component) => {
        const componentShowId = component.getAttribute('show-id');
        return componentShowId === showId;
      });

    if (!isSeoForInlinePlayer) {
      // Try to get the URL from the canonical link element or fallback to current page URL
      const canonicalUrl = new URL(getUrl());

      const pageUrl = new URL(canonicalUrl.toString()); // Create a copy to avoid mutating the original canonical URL
      pageUrl.searchParams.set('autoplayLiveShopping', showId);
      const urlWithShowIdContext = pageUrl.toString();

      // Empty query parameters from canonical
      canonicalUrl.search = '';

      const allVideoObjects = topVideoObject ? [
        topVideoObject,
        ...(Array.isArray(topVideoObject.hasPart) ? topVideoObject.hasPart.filter(p => p['@type'] === 'VideoObject') : []),
      ] : [];

      // Point every VideoObject's url to the merchant page so crawlers link back to where the video is presented.
      for (const videoObject of allVideoObjects) {
        videoObject['url'] = urlWithShowIdContext;
      }

      // mainEntityOfPage only belongs on the top-level VideoObject.
      if (topVideoObject) {
        topVideoObject['mainEntityOfPage'] = {
          '@type': 'WebPage',
          '@id': canonicalUrl.toString(), // Use the canonical URL without query parameters as the @id for the page
        };
      }
    }
  } catch (err) {
    if (isDebug) console.warn('Failed to inject VideoObject SEO schema script tag', { showId, err });
    // REMARK: How confused does crawlers get if we lack this "deeplink" for objects which does not directly sit in the DOM already?
  }


  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(schema, null, 4);
  document.head.appendChild(script);
}

export async function fetchAndInjectSEOSchemaForShowId(showId) {
  if (!showId) return;

  // No showId-based pre-fetch dedup: dedup happens inside injectLdJsonSchemaScriptTag once we
  // have the incoming VideoObject's @id to compare against what's on the page. `cachedFnCall`
  // below still collapses concurrent/repeat fetches for the same showId so we don't hammer the
  // backend on retries.
  try {
    const locale = getLocale();

    const url = new URL(`${process.env.WIDGETS_APPENGINE_BASE_URL}/shows/${showId}/bootstrap`);
    if (locale) url.searchParams.append('locale', locale);

    const request = await cachedFnCall(
      `seo-schema-${showId}`,
      () => fetch(url.toString()),
    );

    const response = await request.json();
    if (!response || !response.seo) return;
    const { schema = {} } =  response.seo;

    injectLdJsonSchemaScriptTag(showId, schema);
  } catch (err) {
    if (isDebug) console.warn('Failed to pre-fetch bootstrap data for showId', { showId, err });
  }
}
